/**
 * Client-safe blog list filter helpers (tag / author query params).
 *
 * Keep this module free of `import.meta.glob` / MDX so `"use client"`
 * components can import these functions without pulling server content
 * modules into the client graph (Vite/acorn cannot parse TS generics on
 * `import.meta.glob`).
 */

export type BlogFilterablePost = {
  author: string;
  tags?: string[];
};

/**
 * Normalize a blog tag or author label for `/blog` query params (`tag`, `author`).
 *
 * Rules:
 * - trim leading/trailing whitespace
 * - lowercase
 * - replace runs of whitespace with a single hyphen
 * - drop characters that are not letters, numbers, or hyphens
 * - collapse repeated hyphens and trim edge hyphens
 *
 * Matching compares this normalized form (case-insensitive via lowercase).
 */
export function normalizeBlogFilterSlug(rawLabel: string): string {
  return rawLabel
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildBlogListTagFilterHref(tagLabel: string): string {
  const tagSlug = normalizeBlogFilterSlug(tagLabel);
  return `/blog?tag=${encodeURIComponent(tagSlug)}`;
}

export function buildBlogListAuthorFilterHref(authorName: string): string {
  const authorSlug = normalizeBlogFilterSlug(authorName);
  return `/blog?author=${encodeURIComponent(authorSlug)}`;
}

export function blogPostMatchesTagSlug(
  post: BlogFilterablePost,
  tagSlug: string,
): boolean {
  const normalizedTagSlug = normalizeBlogFilterSlug(tagSlug);
  if (!normalizedTagSlug) {
    return false;
  }
  return (post.tags ?? []).some(
    (tagLabel) => normalizeBlogFilterSlug(tagLabel) === normalizedTagSlug,
  );
}

export function blogPostMatchesAuthorSlug(
  post: BlogFilterablePost,
  authorSlug: string,
): boolean {
  const normalizedAuthorSlug = normalizeBlogFilterSlug(authorSlug);
  if (!normalizedAuthorSlug) {
    return false;
  }
  return normalizeBlogFilterSlug(post.author) === normalizedAuthorSlug;
}

export function getBlogTagLabelForSlug(
  posts: BlogFilterablePost[],
  tagSlug: string,
): string | undefined {
  const normalizedTagSlug = normalizeBlogFilterSlug(tagSlug);
  if (!normalizedTagSlug) {
    return undefined;
  }
  for (const post of posts) {
    for (const tagLabel of post.tags ?? []) {
      if (normalizeBlogFilterSlug(tagLabel) === normalizedTagSlug) {
        return tagLabel;
      }
    }
  }
  return undefined;
}

export function getBlogAuthorNameForSlug(
  posts: BlogFilterablePost[],
  authorSlug: string,
): string | undefined {
  const normalizedAuthorSlug = normalizeBlogFilterSlug(authorSlug);
  if (!normalizedAuthorSlug) {
    return undefined;
  }
  for (const post of posts) {
    if (normalizeBlogFilterSlug(post.author) === normalizedAuthorSlug) {
      return post.author;
    }
  }
  return undefined;
}
